import { useState, useEffect, useCallback } from 'react';
import { styled } from 'styled-components';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';

/**
 * Componente para seleccionar horas y minutos
 * Convierte automáticamente a valor decimal (ej: 1h 30min = 1.5)
 * @param {Function} actionHandler - Función callback que recibe el valor decimal
 * @param {number} defaultValue - Valor decimal inicial (ej: 1.5 para 1h 30min)
 * @param {number} maxHours - Máximo de horas permitidas (por defecto 24)
 * @param {boolean} disabled - Si el componente está deshabilitado
 */
const CustomHoursPicker = ({ actionHandler, defaultValue = null, maxHours = 24, disabled = false }) => {
    const getHoursAndMinutesFromDecimal = (decimalValue) => {
        // Si no hay valor o es null/undefined, retornar valores vacíos
        if (decimalValue === null || decimalValue === undefined || decimalValue === 0) {
            return { hours: '', minutes: '' };
        }

        const validValue = typeof decimalValue === 'number' && !isNaN(decimalValue) ? decimalValue : 0;

        if (validValue === 0) {
            return { hours: '', minutes: '' };
        }

        const hours = Math.floor(validValue);
        const minutes = Math.round((validValue - hours) * 60);

        return { hours, minutes };
    };

    const initialValues = getHoursAndMinutesFromDecimal(defaultValue);
    const [hours, setHours] = useState(initialValues.hours);
    const [minutes, setMinutes] = useState(initialValues.minutes);
    const [isInitializing, setIsInitializing] = useState(true);

    // Convertir horas y minutos a decimal
    const convertToDecimal = (h, m) => {
        if (h === '' && m === '') {
            return 0;
        }
        const hoursValue = h === '' ? 0 : h;
        const minutesValue = m === '' ? 0 : m;
        return hoursValue + minutesValue / 60;
    };

    const memoizedActionHandler = useCallback(actionHandler, []);

    // Actualizar valores internos cuando cambie defaultValue
    useEffect(() => {
        const newValues = getHoursAndMinutesFromDecimal(defaultValue);

        if (newValues.hours !== hours || newValues.minutes !== minutes) {
            setHours(newValues.hours);
            setMinutes(newValues.minutes);
            setIsInitializing(false);
        } else if (isInitializing) {
            setIsInitializing(false);
        }
    }, [defaultValue]);

    // Ejecutar actionHandler solo cuando hours/minutes cambien por interacción del usuario
    useEffect(() => {
        if (!isInitializing) {
            const decimalValue = convertToDecimal(hours, minutes);
            memoizedActionHandler(decimalValue);
        }
    }, [hours, minutes, isInitializing, memoizedActionHandler]);

    // Generar opciones de horas
    const generateHourOptions = () => {
        const options = [
            <MenuItem key='empty' value=''>
                -- Seleccionar --
            </MenuItem>,
        ];
        for (let i = 0; i <= maxHours; i++) {
            options.push(
                <MenuItem key={i} value={i}>
                    {i} {i === 1 ? 'hora' : 'horas'}
                </MenuItem>
            );
        }
        return options;
    };

    // Generar opciones de minutos
    const generateMinuteOptions = () => {
        const options = [
            <MenuItem key='empty' value=''>
                -- Seleccionar --
            </MenuItem>,
        ];
        const minuteValues = [0, 15, 30, 45];
        return options.concat(
            minuteValues.map((minute) => (
                <MenuItem key={minute} value={minute}>
                    {minute} {minute === 1 ? 'minuto' : 'minutos'}
                </MenuItem>
            ))
        );
    };

    const handleHoursChange = (event) => {
        setHours(event.target.value);
    };

    const handleMinutesChange = (event) => {
        setMinutes(event.target.value);
    };

    return (
        <HoursPickerContainer>
            <StyledFormControl size='small' disabled={disabled}>
                <InputLabel>Horas</InputLabel>
                <Select
                    value={hours}
                    onChange={handleHoursChange}
                    label='Horas'
                    sx={{
                        height: 40,
                        '& .MuiSelect-select': {
                            padding: '8.5px 14px',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiInputBase-root': {
                            height: 40,
                        },
                    }}
                >
                    {generateHourOptions()}
                </Select>
            </StyledFormControl>

            <StyledFormControl size='small' disabled={disabled}>
                <InputLabel>Minutos</InputLabel>
                <Select
                    value={minutes}
                    onChange={handleMinutesChange}
                    label='Minutos'
                    sx={{
                        height: 40,
                        '& .MuiSelect-select': {
                            padding: '8.5px 14px',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiInputBase-root': {
                            height: 40,
                        },
                    }}
                >
                    {generateMinuteOptions()}
                </Select>
            </StyledFormControl>

            <ValueDisplay>{hours !== '' || minutes !== '' ? `${convertToDecimal(hours, minutes).toFixed(2)} hrs` : '--'}</ValueDisplay>
        </HoursPickerContainer>
    );
};

// Styled Components
const HoursPickerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 40px;
`;

const ValueDisplay = styled.span`
    font-size: 12px;
    color: #6c757d;
    font-weight: 500;
    min-width: 60px;
    white-space: nowrap;
`;

const StyledFormControl = styled(FormControl)`
    flex: 1;

    & .MuiInputLabel-root {
        font-weight: normal !important;
    }
`;

export default CustomHoursPicker;
